import { useCallback, type KeyboardEvent, type SyntheticEvent } from "react";
import {
    usePickerLevel,
    type PickerLevelContextValue,
} from "./PickerChainContext";

const isCaretAtStart = (target: EventTarget | null) =>
    !(target instanceof HTMLInputElement) ||
    target.value === "" ||
    target.selectionStart === 0;

const isCaretAtEnd = (target: EventTarget | null) =>
    !(target instanceof HTMLInputElement) ||
    target.selectionEnd === target.value.length;

/** Base UI hands its merged handlers an event that can veto its own handler. */
export const preventBaseUIHandler = (event: SyntheticEvent<HTMLElement>) => {
    if (
        "preventBaseUIHandler" in event &&
        typeof event.preventBaseUIHandler === "function"
    ) {
        event.preventBaseUIHandler();
    }
};

const highlightedFlyoutHandle = (level: PickerLevelContextValue) => {
    const highlighted = level.popupRef.current?.querySelector(
        "[data-highlighted][data-flyout-id]",
    );
    const flyoutId = highlighted?.getAttribute("data-flyout-id");
    return flyoutId === null || flyoutId === undefined
        ? undefined
        : level.flyouts.current.get(flyoutId);
};

/** The highlighted tree parent's disclosure, when it is `expanded` (`"true"`) or collapsed (`"false"`). */
const highlightedExpandToggle = ({
    level,
    expanded,
}: {
    level: PickerLevelContextValue;
    expanded: "true" | "false";
}) => {
    const toggle = level.popupRef.current?.querySelector(
        `[data-highlighted] [data-picker-part="expand-toggle"][aria-expanded="${expanded}"]`,
    );
    return toggle instanceof HTMLElement ? toggle : undefined;
};

/**
 * Cascading-level keys, attached wherever a level receives keyboard input
 * (its popup, its search, an input trigger): ArrowRight and Enter open the
 * highlighted flyout row (Enter is taken away from Base UI so a parent row
 * is never selected, and from ancestors such as a grid cell editor that
 * would finish on it), ArrowLeft closes a flyout level and returns focus to
 * its row. ArrowRight expands a highlighted collapsed tree parent and
 * ArrowLeft collapses an expanded one. Inside a search field the arrows only
 * act once the caret is at the matching end, so text editing keeps them.
 */
export const usePickerLevelKeyboard = () => {
    const level = usePickerLevel();

    return useCallback(
        (event: KeyboardEvent<HTMLElement>) => {
            if (level === null || event.defaultPrevented) {
                return;
            }
            if (event.key === "ArrowRight" || event.key === "Enter") {
                const handle = highlightedFlyoutHandle(level);
                if (handle !== undefined) {
                    event.preventDefault();
                    event.stopPropagation();
                    preventBaseUIHandler(event);
                    handle.open();
                    return;
                }
                const collapsedToggle =
                    event.key === "ArrowRight" && isCaretAtEnd(event.target)
                        ? highlightedExpandToggle({ level, expanded: "false" })
                        : undefined;
                if (collapsedToggle !== undefined) {
                    event.preventDefault();
                    collapsedToggle.click();
                }
                return;
            }
            if (event.key !== "ArrowLeft" || !isCaretAtStart(event.target)) {
                return;
            }
            const expandedToggle = highlightedExpandToggle({
                level,
                expanded: "true",
            });
            if (expandedToggle !== undefined) {
                event.preventDefault();
                expandedToggle.click();
                return;
            }
            if (level.closeLevel !== undefined) {
                event.preventDefault();
                level.closeLevel();
            }
        },
        [level],
    );
};
